// The Ministry behind the title screen and lobby: key art (ui/KeyArt.tsx) over a still of the real
// board, rendered ahead of time (`npm run backdrop -w @bcm/client`). The menu costs no WebGL context,
// no shader compilation and no per-frame rendering, so it is instant and smooth on any machine; the
// cards, stamp, paperwork and motes move in CSS on the compositor.
//
// Dev: `/?backdrop=live` (add `&centered=1` for the lobby framing) renders the live 3D board in a
// fixed pose with no UI on top — that is what the capture script photographs.
import { useCallback, useEffect, useRef, useState } from 'react';
import { BoardScene } from '../scene/BoardScene';
import { audio } from '../audio/sfx';
import { KeyArt } from './KeyArt';

const params = new URLSearchParams(location.search);
const LIVE = import.meta.env.DEV && params.get('backdrop') === 'live';
const STAMP = import.meta.env.DEV && params.get('backdrop') === 'stamp';

export function MenuBackdrop({ centered = false }: { centered?: boolean }) {
  const [ready, setReady] = useState(false);
  const onReady = useCallback(() => setReady(true), []);
  useEffect(() => {
    audio.ambienceOn(true);
    return () => audio.ambienceOn(false);
  }, []);
  if (LIVE) return <LiveBoard centered={centered || params.get('centered') === '1'} />;
  if (STAMP) return <StampShot />;
  return (
    <div className={`menu-backdrop ${ready ? 'ready' : ''} ${centered ? 'centered' : ''}`} aria-hidden>
      <KeyArt centered={centered} onReady={onReady} />
      <div className="menu-backdrop-shade" />
    </div>
  );
}

/** The live board in a fixed attract pose, with no shade or UI: the source for the captured images. */
function LiveBoard({ centered }: { centered: boolean }) {
  const mount = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    document.documentElement.classList.add('backdrop-capture');
    const sc = new BoardScene(mount.current!, 'high');
    sc.setKeyArt(centered ? 'lobby' : 'menu');
    void sc.ready.then(() => setReady(true));
    return () => {
      document.documentElement.classList.remove('backdrop-capture');
      sc.dispose();
    };
  }, [centered]);
  return (
    <div className={`menu-backdrop live ${ready ? 'ready' : ''}`} aria-hidden>
      <div className="menu-backdrop-canvas" ref={mount} />
    </div>
  );
}

/** The Stamp of Precedence alone on a transparent canvas: the source of the key art's stamp. */
function StampShot() {
  const mount = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.documentElement.classList.add('backdrop-capture');
    let canvas: HTMLCanvasElement | null = null;
    void import('../scene/stampRender').then(({ renderStamp }) => {
      canvas = renderStamp(1024);
      canvas.id = 'stamp-shot';
      mount.current?.appendChild(canvas);
    });
    return () => canvas?.remove();
  }, []);
  return <div className="menu-backdrop live ready" ref={mount} aria-hidden />;
}
